import {
  FilterInfoOperator,
  FormattingRuleOperator,
  type FormattingOperator,
  type Operator,
} from '@ardoq/api-types';

export enum DateValueKind {
  NONE = 'none',
  SINGLE = 'single',
  PAIR = 'pair',
  RELATIVE = 'relative',
}

export enum DateComparatorFamily {
  DATE = 'date',
  DATE_RANGE = 'dateRange',
}

type DateComparatorDefinition = {
  comparator: FormattingOperator;
  kind: DateValueKind;
  families: DateComparatorFamily[];
};

const { DATE, DATE_RANGE } = DateComparatorFamily;

/**
 * Every date comparator the conditional-formatting editor offers, in the order
 * it offers them: what it takes as its value, and for which kind of field.
 * The editor's comparator lists, its value inputs and the AI's value checks all
 * read this list, so a new date comparator is added here.
 */
const DATE_COMPARATOR_DEFINITIONS: DateComparatorDefinition[] = [
  {
    comparator: FormattingRuleOperator.RELATIVE_DATE,
    kind: DateValueKind.RELATIVE,
    families: [DATE, DATE_RANGE],
  },
  {
    comparator: FilterInfoOperator.DATE_MATCH,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FormattingRuleOperator.DATE_NOT_EQUAL,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FilterInfoOperator.DATE_EARLIER,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FilterInfoOperator.DATE_LATER,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FormattingRuleOperator.DATE_ON_OR_BEFORE,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FormattingRuleOperator.DATE_ON_OR_AFTER,
    kind: DateValueKind.SINGLE,
    families: [DATE],
  },
  {
    comparator: FormattingRuleOperator.DATE_BETWEEN,
    kind: DateValueKind.PAIR,
    families: [DATE],
  },
  {
    comparator: FormattingRuleOperator.DATE_NOT_BETWEEN,
    kind: DateValueKind.PAIR,
    families: [DATE],
  },
  {
    comparator: FilterInfoOperator.DATE_RANGE_BEFORE,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FilterInfoOperator.DATE_RANGE_AFTER,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FormattingRuleOperator.DATE_RANGE_ON_OR_BEFORE,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FormattingRuleOperator.DATE_RANGE_ON_OR_AFTER,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FormattingRuleOperator.DATE_RANGE_BETWEEN,
    kind: DateValueKind.PAIR,
    families: [DATE_RANGE],
  },
  {
    comparator: FilterInfoOperator.DATE_RANGE_CONTAINS,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
    kind: DateValueKind.SINGLE,
    families: [DATE_RANGE],
  },
  {
    comparator: FormattingRuleOperator.IS_EMPTY,
    kind: DateValueKind.NONE,
    families: [DATE, DATE_RANGE],
  },
];

/**
 * What a date comparator takes as its value, or `null` for a comparator that
 * is not a date comparator.
 */
export const getDateValueKind = (
  comparator: Operator | FormattingOperator | undefined
): DateValueKind | null =>
  DATE_COMPARATOR_DEFINITIONS.find(
    definition => definition.comparator === comparator
  )?.kind ?? null;

/**
 * The comparators the editor offers for a date or date-range field. Relative
 * date is offered on its own, in a group before the others.
 */
export const getDateComparatorGroups = (
  family: DateComparatorFamily
): FormattingOperator[][] => {
  const offered = DATE_COMPARATOR_DEFINITIONS.filter(({ families }) =>
    families.includes(family)
  );
  const isRelative = ({ kind }: DateComparatorDefinition): boolean =>
    kind === DateValueKind.RELATIVE;

  return [
    offered.filter(isRelative).map(({ comparator }) => comparator),
    offered
      .filter(definition => !isRelative(definition))
      .map(({ comparator }) => comparator),
  ];
};
